import { Pipe, PipeTransform } from '@angular/core';

@Pipe({ name: 'countryCodeToISOLocale' })
export class CountryCodeToISOLocalePipe implements PipeTransform {
  public transform(countryCode: string): string {
    if (!countryCode) return '';

    // Normalize separators and trim
    const raw = countryCode.trim().replace('-', '_');
    const [p0, p1] = raw.split('_');

    // Validators to avoid passing bad tags to Intl.Locale
    const isLang = (s?: string) => !!s && /^[a-z]{2,3}$/i.test(s);
    const isRegion = (s?: string) => !!s && /^([a-z]{2}|\d{3})$/i.test(s);

    // Default language for a given region (ISO 3166-1)
    const defaultLangByRegion = {
      US: 'en',
      GB: 'en',
      AU: 'en',
      NZ: 'en',
      CA: 'en',
      IE: 'en',
      SG: 'en',
      ZA: 'en',
      DE: 'de',
      AT: 'de',
      CH: 'de', // CH defaults to German
      FR: 'fr',
      BE: 'nl',
      LU: 'fr', // BE -> Dutch by default
      ES: 'es',
      MX: 'es',
      AR: 'es',
      CL: 'es',
      CO: 'es',
      PE: 'es',
      IT: 'it',
      PT: 'pt',
      BR: 'pt',
      CZ: 'cs',
      SK: 'sk',
      PL: 'pl',
      NL: 'nl',
      SE: 'sv',
      NO: 'no',
      DK: 'da',
      FI: 'fi',
      IS: 'is',
      JP: 'ja',
      CN: 'zh',
      TW: 'zh',
      HK: 'zh',
      KR: 'ko',
      RU: 'ru',
      UA: 'uk',
      TR: 'tr',
      GR: 'el',
      RO: 'ro',
      BG: 'bg',
      HU: 'hu',
      HR: 'hr',
      RS: 'sr',
      SI: 'sl',
      BA: 'bs',
      MK: 'mk',
      AL: 'sq',
      ME: 'sr',
      IL: 'he',
      IR: 'fa',
      IN: 'hi',
      PK: 'ur',
      BD: 'bn',
      NP: 'ne',
      LK: 'si',
      TH: 'th',
      VN: 'vi',
      ID: 'id',
      MY: 'ms',
      PH: 'fil',
      AE: 'ar',
      SA: 'ar',
      EG: 'ar',
      MA: 'ar',
      DZ: 'ar',
      TN: 'ar',
      QA: 'ar',
      KW: 'ar',
      JO: 'ar',
      LB: 'ar',
      OM: 'ar',
      BH: 'ar',
      IQ: 'ar',
      KE: 'sw',
      TZ: 'sw',
      UG: 'sw',
      ET: 'am',
      GH: 'en',
      NG: 'en',
    };

    // Default region for a given language (ISO 639-1)
    const defaultRegionByLang = {
      en: 'US',
      es: 'ES',
      pt: 'BR',
      fr: 'FR',
      de: 'DE',
      it: 'IT',
      nl: 'NL',
      cs: 'CZ',
      sk: 'SK',
      pl: 'PL',
      sv: 'SE',
      no: 'NO',
      da: 'DK',
      fi: 'FI',
      is: 'IS',
      zh: 'CN',
      ja: 'JP',
      ko: 'KR',
      ru: 'RU',
      uk: 'UA',
      tr: 'TR',
      el: 'GR',
      ro: 'RO',
      bg: 'BG',
      hu: 'HU',
      hr: 'HR',
      sr: 'RS',
      sl: 'SI',
      bs: 'BA',
      mk: 'MK',
      sq: 'AL',
      he: 'IL',
      fa: 'IR',
      ar: 'SA',
      hi: 'IN',
      ur: 'PK',
      bn: 'BD',
      ne: 'NP',
      si: 'LK',
      th: 'TH',
      vi: 'VN',
      id: 'ID',
      ms: 'MY',
      fil: 'PH',
      sw: 'KE',
      am: 'ET',
    };

    // Derive language/region intent from input
    let language = '';
    let region = '';

    // Case 1: Already language + region (xx_YY)
    if (isLang(p0) && isRegion(p1)) {
      language = p0.toLowerCase();
      region = p1.toUpperCase();
    }

    // Case 2: Single region (e.g., "CZ")
    else if (!p1 && isRegion(p0)) {
      region = p0.toUpperCase();
      language = defaultLangByRegion[region] || region.toLowerCase();
    }

    // Case 3: Single language (e.g., "cs")
    else if (!p1 && isLang(p0)) {
      language = p0.toLowerCase();
      region = defaultRegionByLang[language] || language.toUpperCase();
    }

    // Case 4: Something else — return normalized best-effort
    else {
      // Fallback: try to coerce like "xx_YY" shape without Intl
      const l = (p0 || '').toLowerCase().match(/^[a-z]{2,3}$/)
        ? p0.toLowerCase()
        : '';
      const r = (p1 || '').toUpperCase().match(/^([A-Z]{2}|\d{3})$/)
        ? p1.toUpperCase()
        : l
          ? l.toUpperCase()
          : (p0 || '').toUpperCase();
      if (!l && !r) return 'en_US'; // nothing usable fall back
      language = l || (r ? r.toLowerCase() : 'en');
      region = r || language.toUpperCase();
    }

    // Build a safe BCP47 tag (en-US). We validated parts, so Intl.Locale is safe.
    const bcp47 = `${language}-${region}`;
    const loc = new Intl.Locale(bcp47);

    // Use Intl’s normalization but keep our defaults if region is missing
    const langOut = loc.language || language;
    const regOut = loc.region || region || language.toUpperCase();

    return `${langOut}_${regOut}`;
  }
}
